import { Stack, Card } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { CouponInput } from '@components/CouponInput';
import { CircleLoader } from '@components/Loaders';
import PaymentSummarySection from '@components/PaymentSummarySection';
import RoundedButton from '@components/RoundedButton/index';
import Selector from '@components/Selector';
import SummaryRow from '@components/SummaryRow';

import { PLAN_TEXT } from '@constants/plans';

import type { CostCalculatorCosts } from '@hooks/useCostCalculator';
import { useCostCalculator } from '@hooks/useCostCalculator';

import type { OnboardingMember } from '@models/onboarding-member';

import { formatAddress } from '@utils/format';

import { useGetTaxLazyQuery } from 'generated/stripe.generated';
import type { StripeCoupon } from 'generated/types';
import { PlanType, type TaxRate } from 'generated/types';

import type { SetupPayment } from '.';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  paymentAndBilling: {
    id: 'adminPaymentSummary.paymentAndBilling',
    defaultMessage: 'Payment & billing',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  card: {
    id: 'adminPaymentSummary.card',
    defaultMessage: 'Card: ',
  },
  memberInfo: {
    id: 'adminPaymentSummary.memberInfo',
    defaultMessage: 'Member Information',
  },
  name: {
    id: 'adminPaymentSummary.name',
    defaultMessage: 'Name: ',
  },
  email: {
    id: 'adminPaymentSummary.email',
    defaultMessage: 'Email: ',
  },
  accountType: {
    id: 'adminPaymentSummary.accountType',
    defaultMessage: 'Account Type: ',
  },
  couponCode: {
    id: 'addNewProfile.couponCode',
    defaultMessage: 'Coupon Code',
  },
  invalidCoupon: {
    id: 'addNewProfile.invalidCoupon',
    defaultMessage: 'Invalid Coupon Code',
  },
  couponVerify: {
    id: 'addNewProfile.couponVerify',
    defaultMessage: 'Verify',
  },
  couponError: {
    id: 'addNewProfile.couponError',
    defaultMessage: 'Invalid coupon code',
  },
  monthly: {
    id: 'adminPaymentSummary.monthly',
    defaultMessage: 'Monthly',
  },
  annually: {
    id: 'adminPaymentSummary.annually',
    defaultMessage: 'Annually',
  },
});

interface PaymentSummaryProps {
  members: OnboardingMember[];
  payment?: SetupPayment | null;
  onBack: () => void;
  onSubmit: ({
    costs,
    taxRate,
  }: {
    costs: CostCalculatorCosts;
    taxRate: TaxRate | null;
  }) => Promise<void>;
  loadingStep: string;
  isAddForm?: boolean;
}

const PaymentSummary = ({
  members,
  payment,
  onBack,
  onSubmit,
  loadingStep,
  isAddForm = false,
}: PaymentSummaryProps) => {
  const [getTax, { data: taxData, loading: taxLoading }] = useGetTaxLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState<StripeCoupon | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>('month');

  const taxRate = taxData?.getTax ?? null;
  const loading = taxLoading || submitting;

  const { formatMessage } = useIntl();

  const token = payment?.token;
  const country = payment?.country;
  const currency = country === 'CA' ? 'cad' : 'usd';

  const costs = useCostCalculator({
    members,
    billingPeriod,
    currency,
    coupon: couponCode,
    taxRate: (taxRate && taxRate.percentage / 100) ?? 0,
  });

  useEffect(() => {
    async function fetch() {
      if (!token) {
        return;
      }
      await getTax({ variables: { input: { tokenId: token.id } } });
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleSubmit() {
    setSubmitting(true);
    await onSubmit({ costs, taxRate });
    setSubmitting(false);
  }

  function handleChangePeriod(value: boolean) {
    if (value) {
      setBillingPeriod('year');
    } else {
      setBillingPeriod('month');
    }
  }
  // Return form
  return (
    <>
      <h2>{formatMessage(messages.memberInfo)}</h2>
      {members.map((member, index) => (
        <Card key={index}>
          <SummaryRow
            bottomText={`${member.firstName} ${member.middleName || ''} ${
              member.lastName
            }`}
            topText={formatMessage(messages.name)}
          />
          {Boolean(member.email) && (
            <SummaryRow
              bottomText={member.email}
              topText={formatMessage(messages.email)}
            />
          )}
          <SummaryRow
            bottomText={PLAN_TEXT[member.plan ?? 'CAREGIVER'].title}
            topText={formatMessage(messages.accountType)}
          />
        </Card>
      ))}

      {payment &&
        (!isAddForm ||
          members.some(({ plan }) => plan === PlanType.PATIENT)) && (
          <Stack spacing={1}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                alignContent: 'center',
              }}
            >
              <h2>{formatMessage(messages.paymentAndBilling)}</h2>

              <Selector
                changeSelection={handleChangePeriod}
                isSelected={billingPeriod === 'year'}
                leftLabel={formatMessage(messages.monthly)}
                rightLabel={formatMessage(messages.annually)}
              />
            </div>
            <CouponInput onVerify={setCouponCode} />
            <Card>
              <SummaryRow
                bottomText={payment.name}
                topText={formatMessage(messages.name)}
              />

              <SummaryRow
                bottomText={formatAddress(
                  [payment.line1, payment.line2],
                  payment.city,
                  payment.postalCode,
                  payment.province,
                  payment.country
                )}
                topText={formatMessage(messages.address)}
              />

              <SummaryRow
                bottomText={
                  payment.token?.card
                    ? `${payment.token.card.brand} ${payment.token.card.last4} ${payment.token.card.exp_month}/${payment.token.card.exp_year}`
                    : ''
                }
                topText={payment.token ? formatMessage(messages.card) : ''}
              />
            </Card>

            <Card>
              <PaymentSummarySection costs={costs} />
            </Card>
          </Stack>
        )}

      {loading ? (
        <span style={{ fontWeight: 'bold', padding: 10 }}>{loadingStep}</span>
      ) : null}
      <CircleLoader loading={loading}>
        <RoundedButton disabled={loading} onClick={handleSubmit} type="button">
          {formatMessage(messages.submit)}
        </RoundedButton>

        <RoundedButton disabled={loading} onClick={onBack} type="button">
          {formatMessage(messages.back)}
        </RoundedButton>
      </CircleLoader>
    </>
  );
};

export default PaymentSummary;
